import { useEffect, useMemo, useRef } from 'react';

/**
 * 최소 GFM 렌더러 — 표·코드블록·목록·인용·링크만 다룬다.
 *
 * 하네스 문서를 상세 패널에서 읽기 위한 용도라 이 정도로 충분하고, 마크다운 라이브러리를 넣지 않는다.
 * 입력은 우리 저장소의 문서지만 렌더 전에 전부 escape한다.
 */
/** 이미지 상대경로를 풀 기준 — 그 문서가 어느 저장소의 어느 경로에 있는가. */
export type AssetContext = { source: string; path: string };

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char] ?? char);

/**
 * 이미지 주소를 화면이 걸 수 있는 주소로 바꾼다.
 *
 * 하네스 문서의 이미지는 그 문서 위치 기준 상대경로(`assets/qa-brief/x.png`)다. 저장소가 private이라
 * 원본 주소로는 브라우저가 받아 올 수 없어, 색인이 담아 둔 바이트를 주는 API로 건다.
 *
 * 문서 맥락(`asset`)이 없으면 상대경로를 풀 기준이 없다 — 그때는 원문 그대로 두고 렌더는 실패한다.
 * 조용히 다른 문서의 이미지를 가리키는 것보다 낫다.
 *
 * 경로를 푸는 규칙은 색인 쪽 `DocumentAssetPath`와 같아야 한다 — 색인이 담은 자리와 화면이 거는 자리가
 * 어긋나면 404가 된다. 프런트는 백엔드를 import하지 않는 저장소라 규칙이 두 벌인데, **한쪽을 고치면
 * 다른 쪽도 고친다.**
 */
function imageSource(href: string, asset: AssetContext | undefined): string | null {
  if (/^(https?:)?\/\//i.test(href) || href.startsWith('data:')) return href;
  if (!asset) return null;

  const segments = asset.path.split('/').slice(0, -1);
  for (const segment of href.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      if (segments.length === 0) return null;
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  if (segments.length === 0) return null;

  const resolved = segments.join('/');
  return `/v1/board/assets?source=${encodeURIComponent(asset.source)}&path=${encodeURIComponent(resolved)}`;
}

/** 이미지 파일을 가리키는 주소인가 — 확장자로만 가른다(색인 쪽 `DocumentAssetPath`와 같은 목록). */
function looksLikeImage(href: string): boolean {
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(href.split(/[?#]/)[0]);
}

function image(alt: string, href: string, asset: AssetContext | undefined): string {
  const source = imageSource(href, asset);
  return source ? `<img src="${source}" alt="${alt}" loading="lazy">` : `<em>[image: ${alt || href}]</em>`;
}

function inline(text: string, asset?: AssetContext): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt: string, href: string) => image(alt, href, asset))
    // 이미지 파일을 가리키는 **일반 링크**도 그림으로 편다. 역기획서는 캡처를 첨부 표의 링크로 걸도록
    // 규정돼 있어(`nd-qa-brief`), 임베드만 그리면 그 문서에서는 그림이 하나도 뜨지 않는다.
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label: string, href: string) =>
      looksLikeImage(href) ? image(label, href, asset) : `<a href="${href}" target="_blank" rel="noreferrer">${label}</a>`,
    )
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>');
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

type ListEntry = { indent: number; ordered: boolean; text: string };
type ListFrame = { indent: number; ordered: boolean; items: string[] };

function finalizeListFrame(frame: ListFrame): string {
  const tag = frame.ordered ? 'ol' : 'ul';
  return `<${tag}>${frame.items.map((inner) => `<li>${inner}</li>`).join('')}</${tag}>`;
}

// 들여쓰기 깊이로 중첩 목록을 만든다 — 부모보다 깊으면 새 목록을 열고, 얕아지면 닫아서 부모 항목에 붙인다.
function renderList(entries: ListEntry[], asset?: AssetContext): string {
  const stack: ListFrame[] = [{ indent: entries[0].indent, ordered: entries[0].ordered, items: [] }];

  for (const entry of entries) {
    while (stack.length > 1 && entry.indent < stack[stack.length - 1].indent) {
      const child = stack.pop() as ListFrame;
      const parent = stack[stack.length - 1];
      parent.items[parent.items.length - 1] += finalizeListFrame(child);
    }
    if (entry.indent > stack[stack.length - 1].indent) {
      stack.push({ indent: entry.indent, ordered: entry.ordered, items: [] });
    }
    stack[stack.length - 1].items.push(inline(entry.text, asset));
  }
  while (stack.length > 1) {
    const child = stack.pop() as ListFrame;
    const parent = stack[stack.length - 1];
    parent.items[parent.items.length - 1] += finalizeListFrame(child);
  }
  return finalizeListFrame(stack[0]);
}

function renderTable(lines: string[], asset?: AssetContext): string {
  const rows = lines.map(splitRow);
  if (rows.length < 2) return `<p>${rows.map((row) => inline(row.join(' | '), asset)).join('<br>')}</p>`;
  const head = rows[0].map((cell) => `<th>${inline(cell, asset)}</th>`).join('');
  const body = rows
    .slice(2)
    .map((row) => `<tr>${row.map((cell) => `<td>${inline(cell, asset)}</td>`).join('')}</tr>`)
    .join('');
  return `<div class="tbl-wrap"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
}

export class MarkdownHtml {
  static render(text: string, asset?: AssetContext): string {
    const lines = text.split('\n');
    const out: string[] = [];
    let paragraph: string[] = [];
    let cursor = 0;

    const flush = () => {
      if (paragraph.length > 0) {
        out.push(`<p>${paragraph.map((line) => inline(line, asset)).join('<br>')}</p>`);
        paragraph = [];
      }
    };

    while (cursor < lines.length) {
      const line = lines[cursor].trim();

      if (line.startsWith('```')) {
        flush();
        const lang = line.slice(3).trim().toLowerCase();
        const buffer: string[] = [];
        cursor += 1;
        while (cursor < lines.length && !lines[cursor].trim().startsWith('```')) buffer.push(lines[cursor++]);
        cursor += 1;
        const code = buffer.join('\n');
        // 머메이드는 코드가 아니라 그림이다. 여기서는 자리만 만들고, 실제 렌더는 mount 후 mermaid가 한다
        // (escape해서 넣어도 mermaid는 textContent로 읽으므로 원문이 그대로 전달된다).
        out.push(
          lang === 'mermaid'
            ? `<div class="mermaid-block"><div class="mermaid">${escapeHtml(code)}</div></div>`
            : `<pre><code>${escapeHtml(code)}</code></pre>`,
        );
        continue;
      }

      if (/^#{1,6}\s/.test(line)) {
        flush();
        const level = line.match(/^#+/)?.[0].length ?? 1;
        out.push(`<h${level}>${inline(line.replace(/^#+\s*/, ''), asset)}</h${level}>`);
        cursor += 1;
        continue;
      }

      if (/^(-{3,}|\*{3,})$/.test(line)) {
        flush();
        out.push('<hr>');
        cursor += 1;
        continue;
      }

      if (line.startsWith('|')) {
        flush();
        const buffer: string[] = [];
        while (cursor < lines.length && lines[cursor].trim().startsWith('|')) buffer.push(lines[cursor++].trim());
        out.push(renderTable(buffer, asset));
        continue;
      }

      if (line.startsWith('>')) {
        flush();
        const buffer: string[] = [];
        while (cursor < lines.length && lines[cursor].trim().startsWith('>')) {
          buffer.push(lines[cursor++].trim().replace(/^>\s?/, ''));
        }
        out.push(`<blockquote>${buffer.map((quoted) => inline(quoted, asset)).join('<br>')}</blockquote>`);
        continue;
      }

      if (/^([-*+]|\d+\.)\s/.test(line)) {
        flush();
        const entries: ListEntry[] = [];
        while (cursor < lines.length && /^\s*([-*+]|\d+\.)\s/.test(lines[cursor])) {
          const raw = lines[cursor++];
          const trimmed = raw.trimStart();
          entries.push({
            indent: raw.length - trimmed.length,
            ordered: /^\d+\./.test(trimmed),
            text: trimmed.trimEnd().replace(/^([-*+]|\d+\.)\s*/, ''),
          });
        }
        out.push(renderList(entries, asset));
        continue;
      }

      if (!line) {
        flush();
        cursor += 1;
        continue;
      }

      paragraph.push(line);
      cursor += 1;
    }

    flush();
    return out.join('\n');
  }
}

export function Markdown({ text, asset }: { text: string; asset?: AssetContext }): React.JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const html = useMemo(() => MarkdownHtml.render(text, asset), [text, asset?.source, asset?.path]);

  useEffect(() => {
    const nodes = [...(host.current?.querySelectorAll<HTMLElement>('.mermaid') ?? [])];
    if (nodes.length === 0) return;

    let cancelled = false;
    const RENDER_TIMEOUT_MS = 8000;
    // mermaid.run이 예외 없이 응답이 없는 채로 멈추는 사례가 있어(운영에서 확인, 재현 조건 미확인)
    // 타임아웃으로 강제 실패 처리한다 — 안 그러면 원문 코드가 "아직 처리 전"과 구분 없이 영영 남는다.
    const withTimeout = <T,>(promise: Promise<T>, label: string): Promise<T> => {
      let timer: ReturnType<typeof setTimeout>;
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} 시간 초과(${RENDER_TIMEOUT_MS}ms)`)), RENDER_TIMEOUT_MS);
      });
      return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
    };

    void (async () => {
      try {
        const { default: mermaid } = await withTimeout(import('mermaid'), 'mermaid 모듈 로드');
        mermaid.initialize({ startOnLoad: false, theme: 'neutral', securityLevel: 'strict' });
        for (const node of nodes) {
          if (cancelled) return;
          try {
            // run은 노드 하나가 실패하면 나머지도 멈추므로 하나씩 돌린다 — 하네스 문서에는
            // 문법이 깨진 다이어그램도 섞여 있어서, 실패한 것만 원문으로 남기는 게 낫다.
            await withTimeout(mermaid.run({ nodes: [node], suppressErrors: true }), '다이어그램 렌더링');
          } catch (error) {
            console.error('[Markdown] mermaid 렌더링 실패', { diagram: node.textContent, error });
            node.classList.add('mermaid-failed');
          }
        }
      } catch (error) {
        console.error('[Markdown] mermaid 초기화 실패', error);
        nodes.forEach((node) => node.classList.add('mermaid-failed'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [html]);

  return (
    <div
      ref={host}
      // prose-sm으로 본문 타이포를 잡고, 표·코드·인용만 하네스 문서 밀도에 맞게 조정한다.
      className="prose prose-sm max-w-none prose-headings:mt-5 prose-headings:mb-2 prose-p:my-2 prose-table:text-xs prose-th:bg-canvas prose-pre:bg-canvas prose-pre:text-ink prose-pre:border prose-pre:border-line-soft prose-code:before:content-none prose-code:after:content-none prose-a:text-accent"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
